import { useState, useEffect } from 'react';
import {
  X,
  Building2,
  Users,
  Plus,
  Trash2,
  Save,
  Phone,
  Mail,
  MapPin,
  Star,
  DollarSign,
  Calendar,
  Info,
  ChevronRight,
  Edit
} from 'lucide-react';
import api from '../api/config';
import './BusinessPartnerModal.css';

const BusinessPartnerModal = ({ isOpen, onClose, partner, type, openContactsDirectly = false }) => {
  const [formData, setFormData] = useState({
    // Información básica
    legalName: '',
    tradeName: '',
    taxId: '',
    isClient: type === 'client',
    isSupplier: type === 'supplier',

    // Información de contacto
    address: '',
    city: '',
    country: 'Chile',
    phone: '',
    email: '',
    website: '',

    // Información comercial
    industry: '',
    rating: 3,

    // Notas
    notes: '',
    tags: []
  });

  const [contacts, setContacts] = useState([]);
  const [newContact, setNewContact] = useState({
    firstName: '',
    lastName: '',
    position: '',
    department: '',
    email: '',
    phone: '',
    mobile: '',
    isPrimary: false
  });

  const [showContactForm, setShowContactForm] = useState(false);
  const [showContactsList, setShowContactsList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [editingContactIndex, setEditingContactIndex] = useState(null);
  const [originalContactData, setOriginalContactData] = useState(null);

  useEffect(() => {
    if (partner) {
      setFormData({
        ...partner,
        tags: partner.tags || []
      });
      setContacts(partner.contacts || []);
    }
  }, [partner]);

  useEffect(() => {
    if (openContactsDirectly) {
      setShowContactsList(true);
    }
  }, [openContactsDirectly]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.legalName) newErrors.legalName = 'Razón social es requerida';
    if (!formData.isClient && !formData.isSupplier) {
      newErrors.type = 'Debe ser cliente o proveedor (o ambos)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSaving(true);
    try {
      const dataToSave = {
        ...formData,
        contacts: contacts
      };

      if (partner) {
        await api.put(`/business-partners/${partner.id}`, dataToSave);
      } else {
        await api.post('/business-partners', dataToSave);
      }

      onClose();
    } catch (error) {
      console.error('Error saving partner:', error);
      alert('Error al guardar. Por favor intente nuevamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddContact = async () => {
    if (!newContact.email) {
      alert('Por favor complete el email del contacto');
      return;
    }

    let updatedContacts;

    if (editingContactIndex !== null) {
      // Editando contacto existente
      updatedContacts = [...contacts];
      updatedContacts[editingContactIndex] = {
        ...newContact,
        isPrimary: contacts[editingContactIndex].isPrimary // Mantener el estado de principal
      };
      setEditingContactIndex(null);
    } else {
      // Agregando nuevo contacto
      const contactToAdd = {
        ...newContact,
        isPrimary: contacts.length === 0
      };
      updatedContacts = [...contacts, contactToAdd];
    }

    setContacts(updatedContacts);

    // Si está en modo de contactos directos, guardar automáticamente
    if (openContactsDirectly && partner) {
      try {
        const dataToSave = {
          ...partner,
          contacts: updatedContacts
        };
        await api.put(`/business-partners/${partner.id}`, dataToSave);
      } catch (error) {
        console.error('Error saving contact:', error);
        alert('Error al guardar el contacto');
        // Revertir el cambio si hay error
        setContacts(contacts);
        return;
      }
    }

    setNewContact({
      firstName: '',
      lastName: '',
      position: '',
      department: '',
      email: '',
      phone: '',
      mobile: '',
      isPrimary: false
    });
    setEditingContactIndex(null);
    setOriginalContactData(null);
    setShowContactForm(false);
  };

  const handleDeleteContact = async (index) => {
    const wasPrimary = contacts[index].isPrimary;
    const newContacts = contacts.filter((_, i) => i !== index);

    // Si eliminamos el contacto principal y quedan otros, hacer el primero principal
    if (wasPrimary && newContacts.length > 0) {
      newContacts[0].isPrimary = true;
    }

    setContacts(newContacts);

    // Si está en modo de contactos directos, guardar automáticamente
    if (openContactsDirectly && partner) {
      try {
        const dataToSave = {
          ...partner,
          contacts: newContacts
        };
        await api.put(`/business-partners/${partner.id}`, dataToSave);
      } catch (error) {
        console.error('Error deleting contact:', error);
        alert('Error al eliminar el contacto');
        // Revertir el cambio si hay error
        setContacts(contacts);
        return;
      }
    }
  };

  const handleSetPrimaryContact = async (index) => {
    const newContacts = contacts.map((contact, i) => ({
      ...contact,
      isPrimary: i === index
    }));
    setContacts(newContacts);

    // Si está en modo de contactos directos, guardar automáticamente
    if (openContactsDirectly && partner) {
      try {
        const dataToSave = {
          ...partner,
          contacts: newContacts
        };
        await api.put(`/business-partners/${partner.id}`, dataToSave);
      } catch (error) {
        console.error('Error updating primary contact:', error);
        alert('Error al actualizar el contacto principal');
        // Revertir el cambio si hay error
        setContacts(contacts);
        return;
      }
    }
  };

  const handleEditContact = (index) => {
    const contactToEdit = contacts[index];
    const contactData = {
      firstName: contactToEdit.firstName || '',
      lastName: contactToEdit.lastName || '',
      position: contactToEdit.position || '',
      department: contactToEdit.department || '',
      email: contactToEdit.email || '',
      phone: contactToEdit.phone || '',
      mobile: contactToEdit.mobile || '',
      isPrimary: contactToEdit.isPrimary || false
    };

    setNewContact(contactData);
    setOriginalContactData(contactData); // Guardar datos originales
    setEditingContactIndex(index);
    setShowContactForm(true);
  };

  // Función para verificar si hay cambios en el contacto
  const hasContactChanges = () => {
    if (!originalContactData || editingContactIndex === null) return true; // Para nuevo contacto, siempre habilitado

    return Object.keys(newContact).some(key => {
      return newContact[key] !== originalContactData[key];
    });
  };

  if (!isOpen) return null;

  return (
    <div className="business-modal-overlay">
      <div className="business-modal">
        {/* Header */}
        <div className="business-modal-header">
          <h2>
            {partner ? 'Editar' : 'Nuevo'} {type === 'client' ? 'Cliente' : 'Proveedor'}
          </h2>
          <button className="modal-close" onClick={onClose} type="button">
            ×
          </button>
        </div>

        {/* Content */}
        <div className="business-modal-content">
          {!showContactsList ? (
            <form onSubmit={handleSubmit} className="form-grid">
              {/* Información de la Empresa - ANCHO COMPLETO */}
              <div className="form-section full-width">
                <h3>Información de la Empresa</h3>

                <div className="form-row">
                  <div className="form-group">
                    <label>Razón Social *</label>
                    <input
                      type="text"
                      value={formData.legalName}
                      onChange={(e) => setFormData({...formData, legalName: e.target.value})}
                      className={errors.legalName ? 'error' : ''}
                      placeholder="Empresa S.A."
                    />
                    {errors.legalName && <span className="error-message">{errors.legalName}</span>}
                  </div>
                  <div className="form-group">
                    <label>Nombre de Fantasía</label>
                    <input
                      type="text"
                      value={formData.tradeName}
                      onChange={(e) => setFormData({...formData, tradeName: e.target.value})}
                      placeholder="Nombre comercial"
                    />
                  </div>
                  <div className="form-group">
                    <label>RUT</label>
                    <input
                      type="text"
                      value={formData.taxId}
                      onChange={(e) => setFormData({...formData, taxId: e.target.value})}
                      placeholder="12.345.678-9"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Tipo de Relación</label>
                    <div className="type-selection">
                      <div className="type-checkbox">
                        <input
                          type="checkbox"
                          id="isClient"
                          checked={formData.isClient}
                          onChange={(e) => setFormData({...formData, isClient: e.target.checked})}
                        />
                        <label htmlFor="isClient">Es Cliente</label>
                      </div>
                      <div className="type-checkbox">
                        <input
                          type="checkbox"
                          id="isSupplier"
                          checked={formData.isSupplier}
                          onChange={(e) => setFormData({...formData, isSupplier: e.target.checked})}
                        />
                        <label htmlFor="isSupplier">Es Proveedor</label>
                      </div>
                    </div>
                    {errors.type && <span className="error-message">{errors.type}</span>}
                  </div>
                  <div className="form-group">
                    <label>Teléfono</label>
                    <input
                      type="text"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+56 2 2345 6789"
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="contacto@empresa.cl"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Dirección</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      placeholder="Av. Principal 123"
                    />
                  </div>
                  <div className="form-group">
                    <label>Ciudad</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      placeholder="Santiago"
                    />
                  </div>
                  <div className="form-group">
                    <label>País</label>
                    <input
                      type="text"
                      value={formData.country}
                      onChange={(e) => setFormData({...formData, country: e.target.value})}
                      placeholder="Chile"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full-width">
                    <label>Sitio Web</label>
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => setFormData({...formData, website: e.target.value})}
                      placeholder="https://www.empresa.cl"
                    />
                  </div>
                </div>
              </div>

              {/* Información Comercial */}
              <div className="form-section full-width">
                <h3>Información Comercial</h3>

                  <div className="form-row">
                    <div className="form-group full-width">
                      <label>Industria</label>
                      <input
                        type="text"
                        value={formData.industry}
                        onChange={(e) => setFormData({...formData, industry: e.target.value})}
                        placeholder="Tecnología, Minería, etc."
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group full-width">
                      <label>Calificación</label>
                      <div className="rating-input">
                        {[1, 2, 3, 4, 5].map(star => (
                          <Star
                            key={star}
                            size={20}
                            className={star <= formData.rating ? 'filled' : ''}
                            onClick={() => setFormData({...formData, rating: star})}
                            style={{ cursor: 'pointer' }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group full-width">
                      <label>Notas</label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        rows={3}
                        placeholder="Observaciones adicionales..."
                      />
                    </div>
                  </div>
              </div>

              {/* Gestionar Contactos */}
              <div className="form-section full-width contacts-button-wrapper">
                  <button
                    type="button"
                    className="btn-contacts"
                    onClick={() => setShowContactsList(true)}
                  >
                    <div className="btn-contacts-content">
                      <div className="btn-contacts-icon">
                        <Users size={32} />
                      </div>
                      <div className="btn-contacts-text">
                        <h4>Gestionar Contactos</h4>
                        <p>{contacts.length} contacto{contacts.length !== 1 ? 's' : ''} agregado{contacts.length !== 1 ? 's' : ''}</p>
                      </div>
                      <ChevronRight size={24} />
                    </div>
                  </button>
              </div>
            </form>
          ) : (
            // Vista de Contactos
            <div className="contacts-view">
              <div className="contacts-view-header">
                <div className="contacts-header-left">
                  {!openContactsDirectly && (
                    <button
                      type="button"
                      className="btn-back"
                      onClick={() => setShowContactsList(false)}
                    >
                      ← Volver
                    </button>
                  )}
                  <h3>Gestión de Contactos</h3>
                </div>
              </div>

              <div className="contacts-content">
                {!showContactForm && (
                  <button
                    type="button"
                    className="btn-add-contact"
                    onClick={() => setShowContactForm(true)}
                  >
                    <Plus size={16} />
                    Agregar Nuevo Contacto
                  </button>
                )}

                {showContactForm && (
                  <div className="contact-form">
                    <h4>{editingContactIndex !== null ? 'Editar Contacto' : 'Nuevo Contacto'}</h4>
                    <div className="contact-form-grid">
                      <div className="form-group">
                        <label>Nombre</label>
                        <input
                          type="text"
                          value={newContact.firstName}
                          onChange={(e) => setNewContact({...newContact, firstName: e.target.value})}
                          placeholder="Juan"
                        />
                      </div>
                      <div className="form-group">
                        <label>Apellido</label>
                        <input
                          type="text"
                          value={newContact.lastName}
                          onChange={(e) => setNewContact({...newContact, lastName: e.target.value})}
                          placeholder="Pérez"
                        />
                      </div>
                      <div className="form-group">
                        <label>Cargo</label>
                        <input
                          type="text"
                          value={newContact.position}
                          onChange={(e) => setNewContact({...newContact, position: e.target.value})}
                          placeholder="Gerente de Compras"
                        />
                      </div>
                      <div className="form-group">
                        <label>Departamento</label>
                        <input
                          type="text"
                          value={newContact.department}
                          onChange={(e) => setNewContact({...newContact, department: e.target.value})}
                          placeholder="Adquisiciones"
                        />
                      </div>
                      <div className="form-group">
                        <label>Email *</label>
                        <input
                          type="email"
                          value={newContact.email}
                          onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                          placeholder="juan.perez@empresa.cl"
                        />
                      </div>
                      <div className="form-group">
                        <label>Teléfono</label>
                        <input
                          type="text"
                          value={newContact.phone}
                          onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
                          placeholder="+56 2 2345 6789"
                        />
                      </div>
                    </div>

                    <div className="contact-form-actions">
                      <button
                        type="button"
                        className="btn-cancel-contact"
                        onClick={() => {
                          setShowContactForm(false);
                          setEditingContactIndex(null);
                          setOriginalContactData(null);
                          setNewContact({
                            firstName: '',
                            lastName: '',
                            position: '',
                            department: '',
                            email: '',
                            phone: '',
                            mobile: '',
                            isPrimary: false
                          });
                        }}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        className="btn-save-contact"
                        onClick={handleAddContact}
                        disabled={editingContactIndex !== null && !hasContactChanges()}
                      >
{editingContactIndex !== null ? 'Actualizar Contacto' : 'Guardar Contacto'}
                      </button>
                    </div>
                  </div>
                )}

                {!showContactForm && contacts.length > 0 ? (
                  <div className="contacts-list">
                    {contacts.map((contact, index) => (
                      <div key={index} className="contact-card">
                        <div className="contact-card-info">
                          <div className="contact-card-name">
                            {contact.firstName} {contact.lastName}
                            {contact.isPrimary && (
                              <span className="contact-primary-badge">Principal</span>
                            )}
                          </div>
                          <div className="contact-card-details">
                            {contact.position && `${contact.position}`}
                            {contact.department && ` - ${contact.department}`}
                          </div>
                          <div className="contact-card-contact">
                            {contact.email && (
                              <span><Mail size={14} /> {contact.email}</span>
                            )}
                            {contact.phone && (
                              <span><Phone size={14} /> {contact.phone}</span>
                            )}
                          </div>
                        </div>
                        <div className="contact-card-actions">
                          {!contact.isPrimary && (
                            <button
                              type="button"
                              className="btn-set-primary"
                              onClick={() => handleSetPrimaryContact(index)}
                              title="Establecer como principal"
                            >
                              <Star size={16} />
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn-edit-contact"
                            onClick={() => handleEditContact(index)}
                            title="Editar contacto"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            type="button"
                            className="btn-delete-contact"
                            onClick={() => handleDeleteContact(index)}
                            title="Eliminar contacto"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : !showContactForm ? (
                  <div className="no-contacts">
                    <Users size={48} />
                    <p>No hay contactos agregados</p>
                    <p className="text-muted">Agregue contactos para esta empresa</p>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!showContactsList && (
          <div className="business-modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-primary"
              onClick={handleSubmit}
              disabled={saving}
            >
              <Save size={18} />
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessPartnerModal;